import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type {
  CsvAnalyzeResponse,
  CsvImportResponse,
  CsvParseResponse,
  DataSourceCategory,
} from '@shared/schemas';

import { defaultImportName, toggleOverwriteRow } from '../../_shared/domain/file-import';
import type { DataSourceDraft } from '../domain/data-source';
import type { FileImportError } from '../domain/file-import-error';
import { type FileImportState, initialState } from './file-import.state';

/**
 * Editing a field clears its own validation message only; the error goes once
 * no field is left in it. Transport errors (`api`, `network`) stay. Untouched
 * when none of the keys is in it: a new error object would replay the page's
 * snackbar on every keystroke.
 */
function clearFieldErrors(state: FileImportState, keys: readonly string[]) {
  if (state.error?.kind !== 'validation') return;
  const current = state.error.fields;
  if (!keys.some((key) => key in current)) return;
  const fields = { ...current };
  for (const key of keys) delete fields[key];
  state.error = Object.keys(fields).length > 0 ? { kind: 'validation', fields } : null;
}

/**
 * File-import slice. Reducers are pure state transitions only; every side
 * effect (parse/analyze/import API calls) lives in the saga, which dispatches
 * the `*Started` / `*Succeeded` / `*Failed` transitions below as it progresses.
 */
const slice = createSlice({
  name: 'fileImport',
  initialState,
  reducers: {
    /**
     * Reset the whole flow for the mounting route. Mandatory on page mount:
     * one slice serves both the admin and client routes, so a finished import
     * must not leak into the next visit.
     */
    formInitialized: (state, action: PayloadAction<{ listId: string | null }>) => ({
      ...initialState,
      listId: action.payload.listId,
    }),

    // ── Upload ──
    importNameChanged: (state, action: PayloadAction<string>) => {
      state.importName = action.payload;
      // Typing fixes the "name required" validation error.
      clearFieldErrors(state, ['importName']);
    },
    /** A new category starts with no tool and no text (#749). */
    dataSourceCategoryChanged: (state, action: PayloadAction<DataSourceCategory>) => {
      state.dataSource = { category: action.payload, tool: null, detail: '' };
      // A new category is a new pair: its tool and text are asked afresh.
      clearFieldErrors(state, ['dataSourceCategory', 'dataSourceTool', 'dataSourceDetail']);
    },
    dataSourceToolChanged: (state, action: PayloadAction<string>) => {
      state.dataSource.tool = action.payload;
      // Whether a text is required depends on the tool.
      clearFieldErrors(state, ['dataSourceTool', 'dataSourceDetail']);
    },
    dataSourceDetailChanged: (state, action: PayloadAction<string>) => {
      state.dataSource.detail = action.payload;
      clearFieldErrors(state, ['dataSourceDetail']);
    },
    /**
     * The licence's last import, once the form is reset (#749). Client mode
     * only, and never over a choice already made while it loaded.
     */
    dataSourcePrefilled: (state, action: PayloadAction<DataSourceDraft>) => {
      if (state.listId !== null || state.dataSource.category !== null) return;
      state.dataSource = action.payload;
    },
    parseStarted: (state, action: PayloadAction<{ fileName: string }>) => {
      state.parsing = true;
      state.fileName = action.payload.fileName;
      state.error = null;
      // Client mode: auto-fill the import name from the filename, once.
      if (!state.listId && !state.importName) {
        state.importName = defaultImportName(action.payload.fileName);
      }
    },
    parseSucceeded: (state, action: PayloadAction<CsvParseResponse>) => {
      state.parsing = false;
      state.parseResult = action.payload;
      state.columnMapping = action.payload.auto_mapping;
      state.step = 1;
    },
    parseFailed: (state, action: PayloadAction<FileImportError>) => {
      state.parsing = false;
      state.error = action.payload;
    },

    // ── Map columns ──
    columnMappingChanged: (state, action: PayloadAction<{ header: string; field: string }>) => {
      state.columnMapping[action.payload.header] = action.payload.field;
    },

    // ── Analyze ──
    analyzeStarted: (state) => {
      state.analyzing = true;
      state.error = null;
    },
    analyzeSucceeded: (state, action: PayloadAction<CsvAnalyzeResponse>) => {
      state.analyzing = false;
      state.analyzeResult = action.payload;
      // A new analysis is a new set of conflicts: nothing is overwritten by default.
      state.overwriteRows = [];
      state.step = 2;
    },
    analyzeFailed: (state, action: PayloadAction<FileImportError>) => {
      state.analyzing = false;
      state.error = action.payload;
    },
    isEnrichedToggled: (state, action: PayloadAction<boolean>) => {
      state.isEnriched = action.payload;
    },
    /** One conflict's overwrite, on or off (#709). */
    overwriteRowToggled: (state, action: PayloadAction<number>) => {
      state.overwriteRows = toggleOverwriteRow(state.overwriteRows, action.payload);
    },

    // ── Import ──
    importStarted: (state) => {
      state.importing = true;
      state.error = null;
    },
    importSucceeded: (state, action: PayloadAction<CsvImportResponse>) => {
      state.importing = false;
      state.importResult = action.payload;
      state.step = 3;
    },
    importFailed: (state, action: PayloadAction<FileImportError>) => {
      state.importing = false;
      state.error = action.payload;
    },

    wentBack: (state) => {
      state.step = Math.max(0, state.step - 1);
    },
    errorCleared: (state) => {
      state.error = null;
    },
  },
});

export const fileImportActions = slice.actions;
export const fileImportReducer = slice.reducer;
